import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { j5n5Api, PendingItem } from '../../services/j5n5Api';
import { useJ5N5Reversal } from './hooks/useJ5N5Reversal';
import { BatchAcceptanceBridge, pwj5n5f1AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;

// ============================================================
// Helpers
// ============================================================

function renderStatus(status: string): React.ReactNode {
  if (!status || status === '') return <Tag color="default">未送審</Tag>;
  if (status === 'P') return <Tag color="blue">已過帳</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="orange">{status}</Tag>;
}

// ============================================================
// Main Component
// ============================================================

const J5N5Reversal: React.FC = () => {
  const {
    loading,
    reversing,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchReverse,
  } = useJ5N5Reversal();

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: <span data-contract-field="fj610_01">單號</span>,
      dataIndex: 'doc_no',
      key: 'doc_no',
      width: 100,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="fj610_14">託工名稱</span>,
      dataIndex: 'subcontract_name',
      key: 'subcontract_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fj610_12">廠商代號</span>,
      dataIndex: 'vendor_code',
      key: 'vendor_code',
      width: 130,
    },
    {
      title: <span data-contract-field="fj610_11">處理日期</span>,
      dataIndex: 'process_date',
      key: 'process_date',
      width: 120,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '—',
    },
    {
      title: <span data-contract-field="fj610_80">狀態</span>,
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  // ============================================================
  // Row selection (BR-R04: pre-select based on default_checked)
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter form
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'docStart' | 'docEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>研發託工單核准沖銷 (pwj5n5f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="fj610_01">單號起始</span>}>
            <Input
              value={filter.docStart}
              onChange={(e) => handleFilterChange('docStart', e.target.value)}
              placeholder="例：J24001"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="fj610_01">單號結束</span>}>
            <Input
              value={filter.docEnd}
              onChange={(e) => handleFilterChange('docEnd', e.target.value)}
              placeholder="例：J24099"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆託工單？沖銷後狀態將設為 N，無法還原。`}
              onConfirm={batchReverse}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={reversing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="doc_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 700 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwj5n5f1AcceptanceManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.doc_no}
        queryExact={async (key) => (await j5n5Api.getPending({ docStart: key, docEnd: key })).items?.[0] || null}
        lastApiResponse={pendingItems}
        loading={loading || reversing}
        onAutoFill={() => pendingItems[0] && setSelectedKeys([pendingItems[0].doc_no])}
        autoFillDisabled={pendingItems.length === 0}
        autoFillReason="只選取正式 API 回傳的第一筆研發託工沖銷候選。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default J5N5Reversal;
